import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { locationService } from '../../../services/locationService';
import './AdminLocations.css';

// Компонент формы добавления новой локации
const AdminLocationAdd = () => {
  const navigate = useNavigate();
  
  // Основное состояние
  const [formData, setFormData] = useState({
    name: '',                              
    description: '',                       
    additionalInfo: [{ title: '', description: '' }], 
    points: [{
      name: '',
      description: '',
      coordinates: '', // Одно поле для координат в формате "55.751244, 37.618423"
      photos: []
    }]
  });
  
  const [submitting, setSubmitting] = useState(false); 
  const [error, setError] = useState(null);             
  const [coordinateErrors, setCoordinateErrors] = useState({});
  
  // Состояния для фотографий
  const [tempPhotos, setTempPhotos] = useState([]);           
  const [tempMainPhoto, setTempMainPhoto] = useState(null);   
  const [mainPhotoPreview, setMainPhotoPreview] = useState(null); 
  const [tempPointPhotos, setTempPointPhotos] = useState({});  // Объект: { индекс_точки: [File, File, ...] }
  
  // Обработчики основных полей
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Обработчик основного фото
  const handleMainPhotoSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setTempMainPhoto(file);
      const previewUrl = URL.createObjectURL(file);
      setMainPhotoPreview(previewUrl);
    }
  };

  // Удаление основного фото
  const removeMainPhoto = () => {
    if (mainPhotoPreview) {
      URL.revokeObjectURL(mainPhotoPreview);
    }
    setTempMainPhoto(null);
    setMainPhotoPreview(null);
  };

  // Изменение доп. информации
  const handleAdditionalInfoChange = (index, field, value) => {
    const updatedInfo = [...formData.additionalInfo];
    updatedInfo[index][field] = value;
    setFormData(prev => ({ ...prev, additionalInfo: updatedInfo }));
  };

  // Добавление доп. информации
  const addAdditionalInfo = () => {
    setFormData(prev => ({
      ...prev,
      additionalInfo: [...prev.additionalInfo, { title: '', description: '' }]
    }));
  };

  // Удаление доп. информации
  const removeAdditionalInfo = (index) => {
    if (formData.additionalInfo.length > 1) {
      const updatedInfo = formData.additionalInfo.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, additionalInfo: updatedInfo }));
    }
  };

  // ========== ФУНКЦИИ ДЛЯ РАБОТЫ С ТОЧКАМИ ==========
  
  // Обновление информации о точке
  const updatePointInfo = (index, field, value) => {
    const updatedPoints = [...formData.points];
    updatedPoints[index][field] = value;
    setFormData(prev => ({ ...prev, points: updatedPoints }));
    
    // Если изменяются координаты, очищаем ошибку для этой точки
    if (field === 'coordinates') {
      setCoordinateErrors(prev => ({
        ...prev,
        [index]: null
      }));
    }
  };

  // Добавление новой точки
  const addPoint = () => {
    setFormData(prev => ({
      ...prev,
      points: [
        ...prev.points,
        {
          name: '',
          description: '',
          coordinates: '',
          photos: []
        }
      ]
    }));
  };

  // Удаление точки
  const removePoint = (index) => {
    if (formData.points.length > 1) {
      if (window.confirm('Удалить эту точку?')) {
        setFormData(prev => ({
          ...prev,
          points: prev.points.filter((_, i) => i !== index)
        }));
        
        // Удаляем ошибки координат для этой точки
        setCoordinateErrors(prev => {
          const newState = { ...prev };
          delete newState[index];
          return newState;
        });
        
        // Удаляем временные фото для этой точки
        setTempPointPhotos(prev => {
          const newState = { ...prev };
          delete newState[index];
          return newState;
        });
      }
    }
  };

  // Парсинг строки координат (формат: "55.751244, 37.618423" или "55.751244,37.618423")
  const parseCoordinates = (coordString) => {
    if (!coordString || typeof coordString !== 'string') return null;
    
    // Удаляем лишние пробелы и разбиваем по запятой
    const parts = coordString.trim().split(',').map(part => part.trim());
    
    if (parts.length !== 2) return null;
    
    const lat = parseFloat(parts[0]);
    const lng = parseFloat(parts[1]);
    
    if (isNaN(lat) || isNaN(lng)) return null;
    if (lat < -90 || lat > 90) return null;
    if (lng < -180 || lng > 180) return null;
    
    return { latitude: lat, longitude: lng };
  };

  // Валидация всех координат точек
  const validateCoordinates = () => {
    const errors = {};
    let isValid = true;
    
    formData.points.forEach((point, index) => {
      if (!point.coordinates || point.coordinates.trim() === '') {
        errors[index] = 'Введите координаты';
        isValid = false;
      } else {
        const parsed = parseCoordinates(point.coordinates);
        if (!parsed) {
          errors[index] = 'Некорректный формат. Используйте формат: 55.751244, 37.618423';
          isValid = false;
        }
      }
    });
    
    setCoordinateErrors(errors);
    return isValid;
  };

  // Работа с общими фотографиями локации 
  const handlePhotoSelect = (event) => {
    const files = Array.from(event.target.files);
    setTempPhotos(prev => [...prev, ...files]);
  };

  const removeTempPhoto = (index) => {
    setTempPhotos(prev => prev.filter((_, i) => i !== index));
  };

  // Работа с фотографиями точек
  const handlePointPhotoSelect = (pointIndex, files) => {
    const filesArray = Array.from(files);
    setTempPointPhotos(prev => ({
      ...prev,
      [pointIndex]: [...(prev[pointIndex] || []), ...filesArray]
    }));
  };

  const removePointTempPhoto = (pointIndex, photoIndex) => {
    setTempPointPhotos(prev => ({
      ...prev,
      [pointIndex]: prev[pointIndex].filter((_, i) => i !== photoIndex)
    }));
  };

  // Загрузка всех фотографий после создания локации 
  const uploadAllPhotos = async (locationId, createdPoints) => {
    let mainPhotoId = null;
    
    // 1. Загрузка основной фотографии
    if (tempMainPhoto) {
      const uploaded = await locationService.uploadMainPhoto(locationId, tempMainPhoto);
      mainPhotoId = uploaded.id;
      console.log('Основная фотография загружена, ID:', mainPhotoId);
    }
    
    // 2. Загружаем общие фотографии локации
    let uploadedPhotoIds = [];
    if (tempPhotos.length > 0) {
      const uploaded = await locationService.uploadLocationPhotos(locationId, tempPhotos);
      const uploadedArray = Array.isArray(uploaded) ? uploaded : [uploaded];
      uploadedPhotoIds = uploadedArray.map(p => p.id).filter(id => id != null);
      console.log('Общих фотографий загружено:', uploadedPhotoIds.length);
    }
    
    // 3. Загружаем фотографии для каждой точки
    for (let i = 0; i < formData.points.length; i++) {
      const pointFiles = tempPointPhotos[i] || [];
      if (pointFiles.length > 0 && createdPoints && createdPoints[i]) {
        const pointId = createdPoints[i].id;
        await locationService.uploadPointPhotos(pointId, pointFiles);
        console.log(`Для точки ${i} загружено фотографий:`, pointFiles.length);
      }
    }
    
    return { mainPhotoId, uploadedPhotoIds };
  };

  // Основной обработчик отправки формы
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Валидация названия локации
    if (!formData.name.trim()) {
      setError('Пожалуйста, введите название локации');
      return;
    }
    
    // Валидация координат всех точек
    if (!validateCoordinates()) {
      setError('Пожалуйста, исправьте ошибки в координатах точек');
      return;
    }
    
    setSubmitting(true);
    setError(null);

    try {
      // 1. Подготовка данных: фильтрация пустых блоков доп. информации
      const additionalInfoData = formData.additionalInfo
        .filter(info => info.title && info.title.trim() !== '')
        .map(info => ({
          title: info.title.trim(),
          description: info.description ? info.description.trim() : ''
        }));
      
      // 2. Подготовка данных точек с парсингом координат из одного поля
      const pointsData = formData.points.map((point) => {
        const parsed = parseCoordinates(point.coordinates);
        
        return {
          name: point.name || '',
          description: point.description || '',
          latitude: parsed.latitude,  // Отдельное поле для широты
          longitude: parsed.longitude, // Отдельное поле для долготы
          photos: []
        };
      });
      
      // 3. Формируем объект локации для отправки
      const locationData = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        additionalInfo: additionalInfoData,
        photos: [],
        points: pointsData
      };
      
      console.log('ОТПРАВКА ДАННЫХ', locationData);
      
      // 4. Создание локации (без фото)
      const createdLocation = await locationService.createLocation(locationData);
      const locationId = createdLocation.id;
      console.log('Локация создана с ID:', locationId);
      
      // 5. Загрузка фотографий
      if (tempMainPhoto || tempPhotos.length > 0 || Object.keys(tempPointPhotos).length > 0) {
        const { uploadedPhotoIds, mainPhotoId } = await uploadAllPhotos(locationId, createdLocation.points);
        
        // 6. Обновление локации, добавление ID загруженных фото
        if (mainPhotoId || uploadedPhotoIds.length > 0) {
          const updateData = {};
          if (mainPhotoId) updateData.main_photo_id = mainPhotoId;
          if (uploadedPhotoIds.length > 0) updateData.photo_ids = uploadedPhotoIds;
          
          await locationService.patchLocation(locationId, updateData);
          console.log('Локация обновлена с фотографиями');
        }
      }
      
      // 7. Успех: редирект на список
      alert('Локация успешно создана!');
      navigate('/admin/locations/list');
    } catch (err) {
      console.error('Ошибка при создании локации:', err);
      setError(err.message || 'Не удалось создать локацию');
    } finally {
      setSubmitting(false);
    }
  };

  // Отрисовка формы
  return (
    <div className="admin-location-form">
      <h2 className="admin-location-form_title">Добавление локации</h2>

      {/* Блок ошибок */}
      {error && (
        <div className="admin-location-form_error">
          <span className="error-icon">⚠️</span> {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="admin-location-form_form">
        
        {/* СЕКЦИЯ 1: ОСНОВНАЯ ИНФОРМАЦИЯ */}
        <div className="admin-location-form_section">
          <h3 className="admin-location-form_section-title">Основная информация</h3>
          
          <div className="admin-location-form_field">
            <label htmlFor="name">
              Название <span className="required">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              disabled={submitting}
              placeholder="Введите название локации"
              autoFocus
            />
          </div>

          <div className="admin-location-form_field">
            <label htmlFor="description">Описание</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="4"
              disabled={submitting}
              placeholder="Введите описание локации"
            />
          </div>
        </div>

        {/* СЕКЦИЯ 2: ОСНОВНАЯ ФОТОГРАФИЯ */}
        <div className="admin-location-form_section">
          <h3 className="admin-location-form_section-title">Основная фотография</h3>
          
          <div className="admin-location-form_main-photo">
            {!mainPhotoPreview ? (
              <div className="main-photo-upload">
                <label className={`btn btn-secondary ${submitting ? 'disabled' : ''}`}>
                  📁 Выберите основную фотографию
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleMainPhotoSelect}
                    style={{ display: 'none' }}
                    disabled={submitting}
                  />
                </label>
                <p className="hint">Рекомендуемый размер: 1200x800px</p>
              </div>
            ) : (
              <div className="photo-item">
                <img src={mainPhotoPreview} alt="Основная фотография" />
                <button 
                  type="button" 
                  onClick={removeMainPhoto} 
                  className="photo-remove"
                  disabled={submitting}
                  title="Удалить"
                >
                  ✕
                </button>
              </div>
            )}
          </div>
        </div>

        {/* СЕКЦИЯ 3: ОБЩИЕ ФОТОГРАФИИ */}
        <div className="admin-location-form_section">
          <h3 className="admin-location-form_section-title">Общие фотографии</h3>
          
          <div className="admin-location-form_photo-upload">
            <label className={`btn btn-secondary ${submitting ? 'disabled' : ''}`}>
              📎 Выберите фотографии
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoSelect}
                style={{ display: 'none' }}
                disabled={submitting}
              />
            </label>
            <p className="hint">Можно выбрать несколько фотографий</p>
          </div>
          
          {/* Превью общих фото */}
          {tempPhotos.length > 0 && (
            <div className="photo-grid">
              {tempPhotos.map((photo, index) => (
                <div key={index} className="photo-item">
                  <img src={URL.createObjectURL(photo)} alt={photo.name} />
                  <button 
                    type="button" 
                    onClick={() => removeTempPhoto(index)} 
                    className="photo-remove"
                    disabled={submitting}
                    title="Удалить"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* СЕКЦИЯ 4: ДОПОЛНИТЕЛЬНАЯ ИНФОРМАЦИЯ */}
        <div className="admin-location-form_section">
          <div className="admin-location-form_section-header">
            <h3 className="admin-location-form_section-title">Дополнительная информация</h3>
            <button 
              type="button" 
              onClick={addAdditionalInfo} 
              className="btn btn-secondary btn-small"
              disabled={submitting}
            >
              + Добавить блок
            </button>
          </div>

          {formData.additionalInfo.map((info, index) => (
            <div key={index} className="info-block">
              <div className="info-block-header">
                <h4>Блок {index + 1}</h4>
                {formData.additionalInfo.length > 1 && (
                  <button 
                    type="button" 
                    onClick={() => removeAdditionalInfo(index)} 
                    className="btn-icon btn-remove"
                    disabled={submitting}
                  >
                    ✕
                  </button>
                )}
              </div>
              
              <div className="admin-location-form_field">
                <label>Заголовок</label>
                <input
                  type="text"
                  value={info.title}
                  onChange={(e) => handleAdditionalInfoChange(index, 'title', e.target.value)}
                  placeholder="Введите заголовок"
                  disabled={submitting}
                />
              </div>

              <div className="admin-location-form_field">
                <label>Описание</label>
                <textarea
                  value={info.description}
                  onChange={(e) => handleAdditionalInfoChange(index, 'description', e.target.value)}
                  placeholder="Введите описание"
                  rows="3"
                  disabled={submitting}
                />
              </div>
            </div>
          ))}
        </div>

        {/* СЕКЦИЯ 5: ТОЧКИ (с одним полем для координат) */}
        <div className="admin-location-form_section">
          <div className="admin-location-form_section-header">
            <h3 className="admin-location-form_section-title">Точки на карте</h3>
            <button 
              type="button" 
              onClick={addPoint} 
              className="btn btn-secondary btn-small"
              disabled={submitting}
            >
              + Добавить точку
            </button>
          </div>

          {/* Список точек */}
          {formData.points.map((point, pointIndex) => (
            <div key={pointIndex} className="point-block">
              <div className="point-block-header">
                <h5>Точка {pointIndex + 1}</h5>
                {formData.points.length > 1 && (
                  <button 
                    type="button" 
                    onClick={() => removePoint(pointIndex)} 
                    className="btn-icon btn-remove"
                    disabled={submitting}
                  >
                    ✕
                  </button>
                )}
              </div>
              
              <div className="admin-location-form_field">
                <label>Название точки</label>
                <input 
                  type="text" 
                  value={point.name} 
                  onChange={(e) => updatePointInfo(pointIndex, 'name', e.target.value)}
                  disabled={submitting}
                  placeholder="Введите название точки"
                />
              </div>
              
              <div className="admin-location-form_field">
                <label>Описание точки</label>
                <textarea 
                  value={point.description} 
                  onChange={(e) => updatePointInfo(pointIndex, 'description', e.target.value)} 
                  rows="2"
                  disabled={submitting}
                  placeholder="Введите описание точки"
                />
              </div>

              {/* ОДНО ПОЛЕ для координат */}
              <div className="admin-location-form_field">
                <label>Координаты</label>
                <input 
                  type="text" 
                  value={point.coordinates} 
                  onChange={(e) => updatePointInfo(pointIndex, 'coordinates', e.target.value)}
                  disabled={submitting}
                  placeholder="55.751244, 37.618423"
                  className={coordinateErrors[pointIndex] ? 'error-input' : ''}
                />
                <p className="hint">Формат: широта, долгота (через запятую и пробел)</p>
                {coordinateErrors[pointIndex] && (
                  <div className="error-message">{coordinateErrors[pointIndex]}</div>
                )}
              </div>

              {/* Фото для конкретной точки */}
              <div className="point-photos-section">
                <label className="btn btn-small btn-secondary">
                  📷 Добавить фото для точки
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => handlePointPhotoSelect(pointIndex, e.target.files)}
                    style={{ display: 'none' }}
                    disabled={submitting}
                  />
                </label>
                
                {tempPointPhotos[pointIndex]?.length > 0 && (
                  <div className="photo-grid point-photos-preview">
                    {tempPointPhotos[pointIndex].map((photo, photoIndex) => (
                      <div key={photoIndex} className="photo-item">
                        <img src={URL.createObjectURL(photo)} alt={photo.name} />
                        <button 
                          type="button"
                          onClick={() => removePointTempPhoto(pointIndex, photoIndex)}
                          className="photo-remove"
                          title="Удалить"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* КНОПКИ ДЕЙСТВИЙ */}
        <div className="admin-location-form_actions">
          <button 
            type="submit" 
            className="btn btn-primary" 
            disabled={submitting || !formData.name.trim()}
          >
            {submitting ? 'Сохранение...' : 'Создать локацию'}
          </button>
          <button 
            type="button" 
            onClick={() => navigate('/admin/locations/list')} 
            className="btn btn-secondary"
            disabled={submitting}
          >
            Отмена
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminLocationAdd;